import React from 'react'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp } from '@fortawesome/free-solid-svg-icons'
import AnimatedSparkles from './AnimatedSparkles'
import { useSoloPilotNudge } from '../hooks/useSoloPilotNudge'
import toast from 'react-hot-toast'
import { FetchRequest } from '@rails/request.js'
import AnimatedSubmitButton from '../features/completions/AnimatedSubmitButton'
import { useDoubleTap } from 'use-double-tap'

interface TiptapChain {
  focus: () => TiptapChain
  deleteSelection: () => TiptapChain
  insertContent: (_html: string) => TiptapChain
  run: () => void
}

interface TiptapEditor {
  state: {
    selection: { _from: number; _to: number } & { from: number; to: number }
    doc: { textBetween: (_from: number, _to: number) => string }
  }
  getHTML: () => string
  commands: {
    setContent: (_html: string) => void
  }
  chain: () => TiptapChain
}

type EditorHost = HTMLElement & { editor?: TiptapEditor }

const PLACEHOLDER_TEXT = 'Ask Solo'
const DOCUMENT_GENERATION_PLACEHOLDER_TEXT = 'Ask Solo to edit'

type Props = {
  clicked: (_initialMessage?: string) => void
}

function FastActionButton({ clicked }: Readonly<Props>) {
  const [inputValue, setInputValue] = React.useState('')
  const [isFocused, setIsFocused] = React.useState(false)
  const [isIdleAnimationActive, setIsIdleAnimationActive] = React.useState(false)
  const [isIdleBorderActive, setIsIdleBorderActive] = React.useState(false)
  const [isOnDocumentGenerationEditor, setIsOnDocumentGenerationEditor] = React.useState(false)
  const isSubmittingRef = React.useRef(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const selectedTextRef = React.useRef<string>('')
  const blurTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const isInteractingWithSliderRef = React.useRef(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isContainerInteractingRef = React.useRef(false)
  const containerInteractTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  const doubleTapBind = useDoubleTap(event => {
    const target = event.target as HTMLElement
    if (target.closest('.actions')) return
    openChat()
  }, 300)

  const { resetTimer } = useSoloPilotNudge({
    onIdle: () => {
      if (!isFocused && !inputValue.trim()) {
        setIsIdleAnimationActive(true)
        setIsIdleBorderActive(true)
      }
    },
    onActivity: () => {
      setIsIdleAnimationActive(false)
      setIsIdleBorderActive(false)
    },
  })

  const hasText = inputValue.trim().length > 0
  const isButtonDisabled = isFocused && !hasText

  const containerClassNames = classNames({
    'pilot-faw-button': true,
    'focused': isFocused,
    'has-text': hasText,
    'idle-border': isIdleBorderActive,
  })

  const buttonClassNames = classNames({
    'circle-btn': true,
    'disabled': isButtonDisabled,
  })

  // Check if current URL matches document generation editor pattern
  React.useEffect(() => {
    const checkURL = () => {
      const pathname = window.location.pathname
      const documentGenerationEditorPattern =
        /^\/document_generations\/\d+\/steps\/document_generation_editor$/
      setIsOnDocumentGenerationEditor(documentGenerationEditorPattern.test(pathname))
    }

    checkURL()

    const handlePopState = () => {
      checkURL()
    }
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])

  const captureSelectedText = () => {
    if (isOnDocumentGenerationEditor) {
      const editorElement = document.querySelector<EditorHost>('#document-editor')
      const editor = editorElement?.editor
      const selection = editor?.state?.selection
      if (editor && selection) {
        const { from, to } = selection
        const selectedText = from !== to ? editor.state.doc.textBetween(from, to) : ''
        selectedTextRef.current = selectedText
      }
    }
  }

  const handleSliderInteraction = () => {
    captureSelectedText()
    if (isOnDocumentGenerationEditor) {
      isInteractingWithSliderRef.current = true
    }
  }

  const handleSubmit = (optionId?: string, sliderValue?: number, sliderLabel?: string) => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current)
      blurTimeoutRef.current = null
    }

    isSubmittingRef.current = true

    if (!optionId) {
      const message = inputValue.trim()
      if (message) {
        clicked(message)
      }
      setInputValue('')
      setIsFocused(false)
      isSubmittingRef.current = false
      return
    }

    if (optionId === 'default') {
      if (isOnDocumentGenerationEditor) {
        const userInput = inputValue.trim() || 'Please suggest improvements to this document'
        handleAIDocumentEdit(optionId, sliderValue || 5, sliderLabel || 'General Edit', userInput)
      } else {
        const message = inputValue.trim()
        if (message) {
          clicked(message)
        }
      }
      setInputValue('')
      setIsFocused(false)
      isSubmittingRef.current = false
      return
    }

    if (isOnDocumentGenerationEditor && optionId && sliderValue && sliderLabel) {
      handleAIDocumentEdit(optionId, sliderValue, sliderLabel, inputValue.trim())
      setInputValue('')
      setIsFocused(false)
      isSubmittingRef.current = false
      return
    }

    isSubmittingRef.current = false
  }

  const handleAIDocumentEdit = async (
    optionId: string,
    sliderValue: number,
    sliderLabel: string,
    userInput: string
  ) => {
    const editorElement = document.querySelector<EditorHost>('#document-editor')
    if (!editorElement?.editor) {
      return
    }

    const editor = editorElement.editor
    const currentContent = editor.getHTML()
    const selectedText = selectedTextRef.current

    const pathname = window.location.pathname
    const documentIdMatch = /\/document_generations\/(\d+)\//.exec(pathname)
    if (!documentIdMatch) {
      return
    }
    const documentId = documentIdMatch[1]

    const editorContainer = document.querySelector<HTMLElement>('#document-editor')
    if (editorContainer) {
      editorContainer.style.opacity = '0.6'
      editorContainer.style.pointerEvents = 'none'
    }

    const performEdit = async (): Promise<string> => {
      try {
        const request = new FetchRequest('POST', `/steps_api/edit_document_with_ai/${documentId}`, {
          responseKind: 'json',
          body: JSON.stringify({
            current_content: currentContent,
            option_id: optionId,
            slider_value: sliderValue,
            slider_label: sliderLabel,
            user_input: userInput,
            selected_text: selectedText,
          }),
        })

        const response = await request.perform()
        const data = await response.json

        if (data.status === 'success') {
          const clearHighlightingEvent = new CustomEvent('clearPersistentHighlight')
          document.dispatchEvent(clearHighlightingEvent)

          if (selectedText && data.selected_edit_content) {
            const currentState = editor.state
            const { from, to } = currentState.selection

            if (from !== to) {
              editor
                .chain()
                .focus()
                .deleteSelection()
                .insertContent(data.selected_edit_content)
                .run()
            } else {
              const updatedContent = currentContent.replace(
                selectedText,
                data.selected_edit_content
              )
              if (updatedContent !== currentContent) {
                editor.commands.setContent(updatedContent)
              } else {
                editor.commands.setContent(data.edited_content)
              }
            }
          } else {
            editor.commands.setContent(data.edited_content)
          }

          return data.edit_summary
        }

        throw new Error(data.message || 'Failed to edit document')
      } finally {
        if (editorContainer) {
          editorContainer.style.opacity = '1'
          editorContainer.style.pointerEvents = 'auto'
        }
        selectedTextRef.current = ''
      }
    }

    toast.promise(performEdit(), {
      loading: 'Editing document with AI...',
      success: (editSummary: string) => editSummary,
      error: 'Sorry! An error occurred while editing the document. Please try again later.',
    })
  }

  const handleArrowClick = (event: React.MouseEvent) => {
    isSubmittingRef.current = true
    event.stopPropagation()
    event.preventDefault()
    if (!inputValue.trim()) {
      openChat()
      isSubmittingRef.current = false
      return
    }
    handleSubmit()
    isSubmittingRef.current = false
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (isOnDocumentGenerationEditor) {
        isSubmittingRef.current = true
        const userInput = inputValue.trim() || 'Please suggest improvements to this document'
        handleAIDocumentEdit('default', 5, 'General Edit', userInput)
        setInputValue('')
        setIsFocused(false)
        isSubmittingRef.current = false
      } else {
        const message = inputValue.trim()
        if (message) {
          handleSubmit()
        }
      }
    }
  }

  const handleContainerPointerDown = (
    event: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => {
    const target = event.target as HTMLElement
    if (target === inputRef.current || target.closest('.actions')) {
      return
    }
    isContainerInteractingRef.current = true
    if (containerInteractTimeoutRef.current) {
      clearTimeout(containerInteractTimeoutRef.current)
      containerInteractTimeoutRef.current = null
    }
    containerInteractTimeoutRef.current = setTimeout(() => {
      isContainerInteractingRef.current = false
      containerInteractTimeoutRef.current = null
    }, 400)
    captureSelectedText()
    if (!isFocused) {
      setIsFocused(true)
      inputRef.current?.focus({ preventScroll: true } as unknown as FocusOptions)
      window.requestAnimationFrame(() => {
        inputRef.current?.focus({ preventScroll: true } as unknown as FocusOptions)
      })
    }
  }

  const handleInputFocus = () => {
    captureSelectedText()
    setIsFocused(true)
  }

  const handleInputBlur = () => {
    // Add a small delay before collapsing the input
    // This allows time for submit actions to set the isSubmittingRef flag
    blurTimeoutRef.current = setTimeout(() => {
      if (!isSubmittingRef.current) {
        // Don't close input if on document editor and interacting with slider options
        if (isOnDocumentGenerationEditor && isInteractingWithSliderRef.current) {
          isInteractingWithSliderRef.current = false
          return
        }
        if (isContainerInteractingRef.current) {
          return
        }
        setIsFocused(false)
      }
      blurTimeoutRef.current = null
    }, 100)
  }

  React.useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isFocused])

  const openChat = () => {
    setIsFocused(false)
    clicked()
  }

  // Handle click outside to close input
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        // Reset slider interaction flag and close input
        isInteractingWithSliderRef.current = false
        if (blurTimeoutRef.current) {
          clearTimeout(blurTimeoutRef.current)
          blurTimeoutRef.current = null
        }
        setIsFocused(false)
      }
    }

    if (isFocused) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isFocused])

  React.useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current)
      }
      if (containerInteractTimeoutRef.current) {
        clearTimeout(containerInteractTimeoutRef.current)
      }
    }
  }, [])

  return (
    <div
      ref={containerRef}
      className={containerClassNames}
      onMouseDownCapture={handleContainerPointerDown}
      onTouchStartCapture={handleContainerPointerDown}
      onMouseDown={handleContainerPointerDown}
      onTouchStart={handleContainerPointerDown}
      {...doubleTapBind}
    >
      {!isFocused && !inputValue ? (
        <div className='title'>
          <div style={{ pointerEvents: 'none' }}>
            <AnimatedSparkles isIdleAnimationActive={isIdleAnimationActive} />
          </div>
          <span style={{ pointerEvents: 'none' }}>
            {isOnDocumentGenerationEditor ? DOCUMENT_GENERATION_PLACEHOLDER_TEXT : PLACEHOLDER_TEXT}
          </span>
        </div>
      ) : (
        <div
          className='input-section'
          onMouseDown={handleContainerPointerDown}
          onTouchStart={handleContainerPointerDown}
        >
          <div style={{ pointerEvents: 'none' }}>
            <AnimatedSparkles isIdleAnimationActive={isIdleAnimationActive} />
          </div>
          <input
            ref={inputRef}
            type='text'
            value={inputValue}
            onChange={e => {
              setInputValue(e.target.value)
              setIsIdleAnimationActive(false)
              setIsIdleBorderActive(false)
              resetTimer()
            }}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={
              isOnDocumentGenerationEditor ? DOCUMENT_GENERATION_PLACEHOLDER_TEXT : PLACEHOLDER_TEXT
            }
            className='pilot-faw-input'
          />
        </div>
      )}
      <div className='actions'>
        {isOnDocumentGenerationEditor ? (
          <AnimatedSubmitButton
            disabled={false}
            loading={false}
            onSubmit={(optionId, sliderValue, sliderLabel) =>
              handleSubmit(optionId, sliderValue, sliderLabel)
            }
            onInteraction={handleSliderInteraction}
          />
        ) : (
          <div
            className={buttonClassNames}
            onMouseDown={handleArrowClick}
            role='button'
            tabIndex={0}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleArrowClick(e as unknown as React.MouseEvent)
              }
            }}
            aria-label='Send'
          >
            <FontAwesomeIcon icon={faArrowUp} />
          </div>
        )}
      </div>
    </div>
  )
}

export default FastActionButton
